import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { useAuth } from './AuthContext';
import merchantService from '../services/merchantService';
import { loadBootstrap, resetBootstrap } from '../services/bootstrapService';

// Create context
const MerchantContext = createContext();

// Custom hook to use the merchant context
export const useMerchant = () => {
  return useContext(MerchantContext);
};

export const MerchantProvider = ({ children }) => {
  const { isAuthenticated, getUserMerchants, currentUser, loading: authLoading } = useAuth();
  const [currentMerchant, setCurrentMerchant] = useState(null);
  const [availableMerchants, setAvailableMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [merchantChangeKey, setMerchantChangeKey] = useState(0);

  const prevUserIdRef = useRef(undefined);
  const prevMerchantCountRef = useRef(undefined);

  const merchantIds = currentUser?.merchant_ids;
  const merchantCount = Array.isArray(merchantIds) ? merchantIds.length : (currentUser?.merchant_count || 0);

  // Initialize merchant context when authenticated or when merchant list changes
  useEffect(() => {
    const userId = currentUser?.id;
    const userChanged = prevUserIdRef.current !== undefined && prevUserIdRef.current !== userId;
    prevUserIdRef.current = userId;

    const merchantCountChanged = prevMerchantCountRef.current !== undefined
      && prevMerchantCountRef.current !== merchantCount;
    prevMerchantCountRef.current = merchantCount;

    if (userChanged) {
      setCurrentMerchant(null);
      setAvailableMerchants([]);
      setMerchantChangeKey(prev => prev + 1);
    }

    const initializeMerchants = async () => {
      // Auth hasn't resolved yet — stay loading rather than reporting "no
      // merchant", which would let route guards run against incomplete state
      // on refresh and bounce the user away from a deep link.
      if (authLoading) {
        setLoading(true);
        return;
      }

      if (!isAuthenticated()) {
        setCurrentMerchant(null);
        setAvailableMerchants([]);
        setLoading(false);
        return;
      }

      const basicMerchants = getUserMerchants();

      if (basicMerchants.length > 0) {
        try {
          setLoading(true);

          // A user switch or merchant-count change means the cached bootstrap
          // (captured at initial load) is stale; refetch it.
          if (userChanged || merchantCountChanged) {
            resetBootstrap();
          }

          // Hydrate from the shared bootstrap request (same round-trip the auth
          // and subscription contexts use) instead of a separate
          // current-merchant + user-merchants fetch.
          const bootstrap = await loadBootstrap();
          const currentMerchantData = bootstrap.merchant;

          const fullMerchants = Array.isArray(bootstrap.merchants) && bootstrap.merchants.length > 0
            ? bootstrap.merchants
            : basicMerchants;

          setAvailableMerchants(fullMerchants);

          setCurrentMerchant(prevMerchant => {
            if (userChanged || merchantCountChanged || !prevMerchant) {
              return currentMerchantData;
            }
            return prevMerchant;
          });

          if (merchantCountChanged) {
            setMerchantChangeKey(prev => prev + 1);
          }
        } catch (initErr) {
          setError('Failed to load merchant details');
          setAvailableMerchants(basicMerchants);
          setCurrentMerchant(prevMerchant => {
            if ((userChanged || merchantCountChanged || !prevMerchant) && basicMerchants.length > 0) {
              return basicMerchants[0];
            }
            return prevMerchant;
          });
        }
      } else {
        setAvailableMerchants([]);
      }

      setLoading(false);
    };

    initializeMerchants();
  }, [isAuthenticated, merchantCount, currentUser?.id, authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Switch to a different merchant
  const switchMerchant = async (merchantId) => {
    try {
      setLoading(true);
      setError(null);

      // Active merchant is changing - invalidate the cached bootstrap so the
      // subscription context re-hydrates for the new merchant.
      resetBootstrap();

      // Call backend API to switch merchant
      const response = await merchantService.switchMerchant(merchantId);
      const updatedMerchant = response.merchant;

      // Update current merchant in context
      setCurrentMerchant(updatedMerchant);

      // Update available merchants list with the updated merchant data
      setAvailableMerchants(prev =>
        prev.map(m => m.id === updatedMerchant.id ? updatedMerchant : m)
      );

      // Increment change key to notify components to refetch
      setMerchantChangeKey(prev => prev + 1);

      return updatedMerchant;
    } catch (error) {
      setError('Failed to switch merchant');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Get current merchant's settings
  const getMerchantSettings = () => {
    return currentMerchant?.settings || null;
  };

  // Check if merchant has feature enabled
  const hasFeature = (featureName) => {
    const settings = getMerchantSettings();
    if (!settings) return false;

    switch (featureName) {
      case 'cash_payment':
        return settings.enable_cash;
      default:
        return false;
    }
  };

  // Get user's role for current merchant
  const getCurrentUserRole = () => {
    if (!currentMerchant) return null;
    // Use the user_role from the merchant data (provided by MerchantDetailSerializer)
    return currentMerchant.user_role || null;
  };

  // Get user's permissions for current merchant
  const getCurrentUserPermissions = () => {
    if (!currentMerchant) return {};
    return currentMerchant.user_permissions || {};
  };

  // Check if user has specific permission for current merchant
  const hasPermission = (permission) => {
    const permissions = getCurrentUserPermissions();
    return permissions[permission] || false;
  };

  // Check if user can manage the current merchant
  const canManageMerchant = () => {
    return hasPermission('can_manage_merchant');
  };

  // Check if user can manage orders for current merchant
  const canManageOrders = () => {
    return hasPermission('can_manage_orders');
  };

  // Check if user can view reports for current merchant
  const canViewReports = () => {
    return hasPermission('can_view_reports');
  };

  const canMarkAsPaid = () => hasPermission('can_mark_as_paid');
  const canStartPreparing = () => hasPermission('can_start_preparing');
  const canCompleteOrder = () => hasPermission('can_complete_order');
  const canCancelOrder = () => hasPermission('can_cancel_order');
  const canAddEditOrderItems = () => hasPermission('can_add_edit_order_items');
  const canEditOrder = () => hasPermission('can_edit_order');
  const canViewAnalytics = () => hasPermission('can_view_analytics');
  const canManageMenu = () => hasPermission('can_manage_menu');
  const canManageTables = () => hasPermission('can_manage_tables');
  const canManagePayments = () => hasPermission('can_manage_payments');
  const canManageDiscounts = () => hasPermission('can_manage_discounts');

  const merchantStatus = currentMerchant?.settings?.status || 'available';

  const updateMerchantStatus = async (status, pauseMode = null) => {
    if (!currentMerchant) return;

    try {
      const payload = { status };
      if (pauseMode) {
        payload.pause_mode = pauseMode;
      }
      const response = await merchantService.updateMerchantStatus(currentMerchant.id, payload);

      setCurrentMerchant(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          status: response.data.status,
          status_changed_at: response.data.status_changed_at,
          pause_until: response.data.pause_until,
        },
      }));

      return response.data;
    } catch (err) {
      setError('Failed to update merchant status');
      throw err;
    }
  };

  // Refresh merchant data
  const refreshMerchantData = async () => {
    if (!currentMerchant) return;

    try {
      setLoading(true);
      const updatedMerchant = await merchantService.getMerchant(currentMerchant.id);
      setCurrentMerchant(updatedMerchant);

      // Update in available merchants list too
      setAvailableMerchants(prev =>
        prev.map(m => m.id === updatedMerchant.id ? updatedMerchant : m)
      );
    } catch (error) {
      setError('Failed to refresh merchant data');
    } finally {
      setLoading(false);
    }
  };

  const refreshMerchantsList = async () => {
    if (!isAuthenticated()) return;

    try {
      setLoading(true);
      const basicMerchants = getUserMerchants();

      if (basicMerchants.length > 0) {
        let fullMerchants = basicMerchants;
        const needsFullData = basicMerchants.some(m => !m.name);
        if (needsFullData) {
          try {
            const profileResponse = await merchantService.getUserMerchants();
            const profileData = profileResponse.data || profileResponse;
            if (profileData?.merchants && Array.isArray(profileData.merchants)) {
              fullMerchants = profileData.merchants;
            }
          } catch (_) {
            // Keep basicMerchants as fallback
          }
        }

        setAvailableMerchants(fullMerchants);

        if (currentMerchant) {
          try {
            const response = await merchantService.getMerchant(currentMerchant.id);
            setCurrentMerchant(response.data || response);
          } catch (_) {
            const stillExists = fullMerchants.find(m => m.id === currentMerchant.id);
            if (!stillExists && fullMerchants.length > 0) {
              setCurrentMerchant(fullMerchants[0]);
            }
          }
        } else if (fullMerchants.length > 0) {
          setCurrentMerchant(fullMerchants[0]);
        }

        setMerchantChangeKey(prev => prev + 1);
      }
    } catch (error) {
      setError('Failed to refresh merchants list');
    } finally {
      setLoading(false);
    }
  };

  const value = {
    currentMerchant,
    availableMerchants,
    loading,
    error,
    switchMerchant,
    getMerchantSettings,
    hasFeature,
    getCurrentUserRole,
    getCurrentUserPermissions,
    hasPermission,
    canManageMerchant,
    canManageOrders,
    canViewReports,
    canMarkAsPaid,
    canStartPreparing,
    canCompleteOrder,
    canCancelOrder,
    canAddEditOrderItems,
    canEditOrder,
    canViewAnalytics,
    canManageMenu,
    canManageTables,
    canManagePayments,
    canManageDiscounts,
    refreshMerchantData,
    refreshMerchantsList,
    merchantChangeKey,
    merchantStatus,
    updateMerchantStatus
  };

  return (
    <MerchantContext.Provider value={value}>
      {children}
    </MerchantContext.Provider>
  );
};
